import {SnackBarActions} from '@store/aviary/aviary.adapter';
import {
  CUSTOM_ERROR_HANDLERS_CREATOR,
  CustomErrorHandlersCreator,
  REFRESH_AND_TRY_AGAIN_ERROR_MESSAGE
} from '@aviary/interceptors/http-error-interceptor.types';
import {HttpContext} from '@angular/common/http';

export function contextWithErrorHandlerForLoadHoldsForCatalogRecords(): HttpContext {
  const handler: CustomErrorHandlersCreator = () => ({
    400: {
      discardOriginalErrorResponse: false,
      actionMappers: [
        {
          // error for requests with catalog id list > 500
          errorCodes: 'GENERAL_INVALID_VALUE',
          actions: SnackBarActions.showError('Number of holds requested exceeded request limit.')
        }
      ]
    }
  });
  return new HttpContext().set(CUSTOM_ERROR_HANDLERS_CREATOR, handler);
}

export function contextWithErrorHandlerForPlaceHold(): HttpContext {
  const handler: CustomErrorHandlersCreator = () => ({
    400: {
      discardOriginalErrorResponse: false,
      actionMappers: [
        {
          // invalid branch selection
          errorCodes: 'BRANCH_INVALID_ID',
          actions: SnackBarActions.showError('Selected pickup branch is not a valid pickup location.')
        },
        {
          // no circulating copies found for requested catalog record
          errorCodes: 'CIRCULATION_INVALID_ITEM_ID',
          actions: SnackBarActions.showError('Cannot place a hold because this record has no circulating copies.')
        },
        {
          // max holds per type limit reached
          errorCodes: 'GENERAL_INVALID_VALUE',
          actions: SnackBarActions.showError('Cannot place hold because the maximum holds allowed for this item type has been reached.')
        },
        {
          // authenticated org does not match the catalog record's org
          errorCodes: 'ORG_MISMATCH',
          actions: SnackBarActions.showError('Catalog record is not available.')
        }
        // errorCodes: 'PATRON_INVALID_STANDING_FOR_ACTION', api may return max hold count in message or a general account blocks message
        // errorCodes: 'ENTITY_ID_NOT_FOUND', api returns different message for missing catalog record or patron category, a fixed message may not be appropriate
        // errorCodes: 'CIRCULATION_RULE_VIOLATION' the api is supplying good specific errors for rule violations, intentionally not overriding it here
      ]
    }
  });
  return new HttpContext().set(CUSTOM_ERROR_HANDLERS_CREATOR, handler);
}

export function contextWithErrorHandlerForUpdateHoldPickupBranch(): HttpContext {
  const handler: CustomErrorHandlersCreator = () => ({
    400: {
      discardOriginalErrorResponse: false,
      actionMappers: [
        {
          // ACTION_BLOCKED: requested hold's patron does not match authenticated patron
          // ENTITY_ID_NOT_FOUND: hold not found
          errorCodes: ['ACTION_BLOCKED', 'ENTITY_ID_NOT_FOUND'],
          actions: SnackBarActions.showError(REFRESH_AND_TRY_AGAIN_ERROR_MESSAGE)
        },
        {
          // invalid pickup branch
          errorCodes: 'BRANCH_INVALID_ID',
          actions: SnackBarActions.showError('The requested branch is not a valid pickup location.')
        },
        {
          // hold not in editable status: requested or paused
          errorCodes: 'HOLD_INVALID_STATUS_FOR_ACTION',
          actions: SnackBarActions.showError('Only requested or paused holds may be edited.')
        },
      ]
    }
  });
  return new HttpContext().set(CUSTOM_ERROR_HANDLERS_CREATOR, handler);
}

export function contextWithErrorHandlerForPauseHold(): HttpContext {
  const handler: CustomErrorHandlersCreator = () => ({
    400: {
      discardOriginalErrorResponse: false,
      actionMappers: [
        {
          // ACTION_BLOCKED: requested hold's patron does not match authenticated patron
          // ENTITY_ID_NOT_FOUND: hold not found
          errorCodes: ['ACTION_BLOCKED', 'ENTITY_ID_NOT_FOUND'],
          actions: SnackBarActions.showError(REFRESH_AND_TRY_AGAIN_ERROR_MESSAGE)
        },
        {
          // no hold ids provided
          errorCodes: 'GENERAL_MISSING_PARAM',
          actions: SnackBarActions.showError('Please select items that you wish to pause and try again.')
        },
        {
          // hold is not in requested status
          errorCodes: 'HOLD_INVALID_STATUS_FOR_ACTION',
          actions: SnackBarActions.showError('Only holds with requested status may be paused.')
        },
      ]
    }
  });
  return new HttpContext().set(CUSTOM_ERROR_HANDLERS_CREATOR, handler);
}

export function contextWithErrorHandlerForResumeHolds(): HttpContext {
  const handler: CustomErrorHandlersCreator = () => ({
    400: {
      discardOriginalErrorResponse: false,
      actionMappers: [
        {
          // requested hold's patron does not match authenticated patron
          errorCodes: 'ACTION_BLOCKED',
          actions: SnackBarActions.showError(REFRESH_AND_TRY_AGAIN_ERROR_MESSAGE)
        },
        {
          // empty hold id list or could not find all holds in list
          errorCodes: 'ENTITY_ID_NOT_FOUND',
          actions: SnackBarActions.showError('Please select items that you wish to resume and try again.')
        },
        {
          // hold is not in paused status or requested status w/ NotWantedBefore date
          errorCodes: 'HOLD_INVALID_STATUS_FOR_ACTION',
          actions: SnackBarActions.showError('Only holds with paused status may be resumed.')
        },
      ]
    }
  });
  return new HttpContext().set(CUSTOM_ERROR_HANDLERS_CREATOR, handler);
}

export function contextWithErrorHandlerForCancelHolds(): HttpContext {
  const handler: CustomErrorHandlersCreator = () => ({
    400: {
      discardOriginalErrorResponse: false,
      actionMappers: [
        {
          // could not find one or more requested Holds
          errorCodes: 'ENTITY_ID_NOT_FOUND',
          actions: SnackBarActions.showError(REFRESH_AND_TRY_AGAIN_ERROR_MESSAGE)
        },
        {
          // missing hold id list
          errorCodes: 'GENERAL_INVALID_VALUE',
          actions: SnackBarActions.showError('Please select items that you wish to cancel and try again.')
        },
        {
          // hold has invalid status for canceling, valid statuses: HELD, TRANSIT, PULLED, PENDING, REQUESTED, PAUSED
          errorCodes: 'HOLD_INVALID_STATUS_FOR_ACTION',
          actions: SnackBarActions.showError('Hold cannot be canceled.')
        },
      ]
    }
  });
  return new HttpContext().set(CUSTOM_ERROR_HANDLERS_CREATOR, handler);
}

export function contextWithErrorHandlerForPauseAllHolds(): HttpContext {
  const handler: CustomErrorHandlersCreator = () => ({
    400: {
      discardOriginalErrorResponse: false,
      actionMappers: [
        {
          // listed on endpoint but couldn't determine a case where these errors could actually be thrown
          errorCodes: ['GENERAL_INVALID_VALUE', 'GENERAL_MISSING_PARAM'],
          actions: SnackBarActions.showError('Failed to pause holds.')
        },
        {
          // invalid pause date, must be blank or after today
          errorCodes: 'HOLD_INVALID_PAUSE_DATE',
          actions: SnackBarActions.showError('Invalid pause date.')
        },
      ]
    }
  });
  return new HttpContext().set(CUSTOM_ERROR_HANDLERS_CREATOR, handler);
}
